import * as React from 'react';
import styled from 'styled-components';
import {COLORS, IconButton, Size} from '..';
import {IconButtonContentStyles, StyledPrimaryIconButton} from '../Button/Iconbutton';
import {NotificationDot} from '../NotificationDot';
import {BREAKPOINTS, ComponentS, ComponentTextStyle} from '../styles';

export type ProfileButtonProps = {
  portraitSrc?: string;
  icon?: React.ReactNode;
  initials?: string;
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  notificationVariant?: 'positive' | 'critical';
  hideOnLowWidth?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> ;

const PortraitOverlay = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${COLORS.black};
  opacity: 0;
  position: absolute;
  top: 0px;
  left: 0px;
`;

export const ProfileButtonContainer = styled.div<{ hideOnLowWidth?: boolean }>`
  width: 48px;
  height: 48px;
  display: inline;
  user-select: none;
  
  ${StyledPrimaryIconButton} {
    display: ${props => (props.hideOnLowWidth ? 'none' : 'inline')};

    ${BREAKPOINTS.MEDIUM} {
      display: inline;
    }

    :disabled {
      img {
        filter: grayscale(100%);
      }

      ${IconButtonContentStyles} {
        background-color: ${COLORS.neutral_100};

        svg, svg path {
          fill: ${COLORS.neutral_300};
        }
      }
    }

    :hover:not(:disabled):not(:active),
    &.hover-state {
      ${PortraitOverlay} {
        opacity: 0.25;
        transition: opacity 100ms ease-in-out;
      }
    }

    :active:not(:disabled),
    &.active-state {
      ${PortraitOverlay} {
        opacity: 0.5;
        transition: opacity 0ms ease-in-out;
      }
    }
  }
`;

const NotificationContainer = styled.div`
  position: absolute;
  left: 30px;
  bottom: 30px;
`;

const ImageWrapper = styled.div`
  width: 36px;
  height: 36px;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const ProfileButton = React.forwardRef<HTMLButtonElement, ProfileButtonProps>(({
                                                                                 portraitSrc,
                                                                                 icon,
                                                                                 initials,
                                                                                 onClick,
                                                                                 hideOnLowWidth,
                                                                                 disabled,
                                                                                 notificationVariant,
                                                                                 className,
                                                                                 tabIndex,
                                                                                 ...rest
                                                                               }, ref) => {
  const renderIcon = () => {
    return (
      (portraitSrc && (
        <ImageWrapper>
          <img src={portraitSrc}/>
          <PortraitOverlay/>
        </ImageWrapper>
      )) ||
      (initials && (
        <ComponentS className="initials" textStyle={ComponentTextStyle.Bold}
                    color={disabled ? COLORS.neutral_300 : COLORS.white}>
          {initials}
        </ComponentS>
      )) || <>{icon}</>
    );
  };

  return (
    <ProfileButtonContainer hideOnLowWidth={hideOnLowWidth}>
      <IconButton ref={ref}
                  className={className}
                  disabled={disabled}
                  action={onClick}
                  variant="primary"
                  tabIndex={tabIndex}
                  shape="circular"
                  hideOnLowWidth={hideOnLowWidth}
                  {...rest}>
        {renderIcon()}
        {notificationVariant && (
          <NotificationContainer>
            <NotificationDot size={Size.Medium} variant={notificationVariant}/>
          </NotificationContainer>
        )}
      </IconButton>
    </ProfileButtonContainer>
  );
});

export default ProfileButton;
